/**
 * Where the analyst writes to the copilot — with what goes with the
 * message: a passage they selected in the Document view ("Ask the
 * copilot"), and the item open in the workbench, offered as a chip they can
 * take or leave. ⌘/Ctrl+Enter sends.
 */

import { useEffect, useState } from 'react';

import {
  ActionIcon,
  Button,
  CloseButton,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconSend } from '@tabler/icons-react';

import type { CopilotFocus, CopilotPassage, CopilotSelection } from '@/types/copilot';
import classes from './Copilot.module.css';

interface CopilotComposerProps {
  passage: CopilotPassage | null;
  onPassageClear: () => void;
  /** The item open in the workbench, offered with the message. */
  focus: CopilotFocus | null;
  /** A reply is running: no message until it ends. */
  busy: boolean;
  /** Ours is running: it can be stopped. */
  canStop: boolean;
  onStop: () => void;
  onSend: (text: string, selection: CopilotSelection | null) => Promise<boolean>;
}

function clip(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

export function CopilotComposer({
  passage,
  onPassageClear,
  focus,
  busy,
  canStop,
  onStop,
  onSend,
}: Readonly<CopilotComposerProps>) {
  const [text, setText] = useState('');
  const [withFocus, setWithFocus] = useState(true);
  const [sending, setSending] = useState(false);

  // A passage handed over with words (a review issue to fix) starts the
  // message; the person sends it, or edits it first.
  useEffect(() => {
    if (passage?.prompt) {
      setText(passage.prompt);
    }
  }, [passage?.prompt]);

  const send = async () => {
    const words = text.trim();
    if (!words || busy || sending) {
      return;
    }
    setSending(true);
    try {
      const sent = await onSend(words, {
        passage,
        focus: withFocus ? focus : null,
      });
      if (sent) {
        setText('');
        onPassageClear();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack gap={6} data-testid="copilot-composer">
      {passage ? (
        <Paper withBorder radius="sm" p={6}>
          <Group gap={4} wrap="nowrap" align="flex-start" justify="space-between">
            <Text size="xs" className={classes.quote} data-testid="copilot-passage">
              “{clip(passage.quote, 200)}”
            </Text>
            <CloseButton size="xs" aria-label="Leave the passage out" onClick={onPassageClear} />
          </Group>
        </Paper>
      ) : null}
      {focus ? (
        <Group gap={4}>
          <Button
            size="compact-xs"
            variant={withFocus ? 'light' : 'subtle'}
            color={withFocus ? 'violet' : 'gray'}
            onClick={() => setWithFocus(!withFocus)}
            data-testid="copilot-focus"
            title={
              withFocus
                ? 'Sent with the message — click to leave it out'
                : 'Send it with the message'
            }
          >
            {withFocus ? 'About' : 'Not about'} the {focus.type.replace(/_/g, ' ')}
            {focus.label ? `: ${clip(focus.label, 40)}` : ''}
          </Button>
        </Group>
      ) : null}
      <Group gap="xs" align="flex-end" wrap="nowrap">
        <Textarea
          style={{ flex: 1 }}
          placeholder={busy ? 'A reply is running…' : 'Ask about this case'}
          autosize
          minRows={2}
          maxRows={8}
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void send();
            }
          }}
          aria-label="Your message to the Assistant"
          data-testid="copilot-input"
        />
        {canStop ? (
          <Button size="xs" variant="default" onClick={onStop} data-testid="copilot-stop">
            Stop
          </Button>
        ) : (
          <ActionIcon
            size="lg"
            variant="filled"
            aria-label="Send"
            disabled={!text.trim() || busy}
            loading={sending}
            onClick={() => void send()}
            data-testid="copilot-send"
          >
            <IconSend size={16} />
          </ActionIcon>
        )}
      </Group>
    </Stack>
  );
}
